/* eslint-disable import/no-import-module-exports */
import {
  CombinedState,
  combineReducers,
  configureStore,
} from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import expireReducer from 'redux-persist-expire';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';

import userReducer, { UserStore } from './reducers/userReducer';
import zoneReducer, { ZoneStore } from './reducers/zoneReducer';
import alertReducer, { AlertStoreProps } from './reducers/alertReducer';
import searchReducer, { ISearchState } from './reducers/searchSlice';
import revenueReducer, { RevenueState } from './reducers/revenueSlice';
import approverCardsSlice, {
  IApproverCardsSlice,
} from './reducers/approverCardsSlice';
import {
  apiApproverPortal,
  apiDocumentsPortal,
  apiOrchestratorPricingCommittee,
  apiPortalPricing,
} from './apiSlices/apiSlices';
import {
  ApproverTaskState,
  domainApproverTaskSlice,
} from '../domains/approver-review/slices/approver-task.slice';
import automationReducer, {
  IAutomationState,
} from './reducers/automationSlice';

type Temp2 = CombinedState<{
  [apiApproverPortal.reducerPath]: ReturnType<typeof apiApproverPortal.reducer>;
  [apiDocumentsPortal.reducerPath]: ReturnType<
    typeof apiDocumentsPortal.reducer
  >;
  [apiOrchestratorPricingCommittee.reducerPath]: ReturnType<
    typeof apiOrchestratorPricingCommittee.reducer
  >;
  [apiPortalPricing.reducerPath]: ReturnType<typeof apiPortalPricing.reducer>;
  userStore: UserStore;
  zoneStore: ZoneStore;
  alertStore: AlertStoreProps;
  cardsStore: IApproverCardsSlice;
  searchStore: ISearchState;
  revenueStore: RevenueState;
  domainApproverTask: ApproverTaskState;
  automationStore: IAutomationState;
}>;

function configureAppStore() {
  const reducer = combineReducers({
    [apiApproverPortal.reducerPath]: apiApproverPortal.reducer,
    [apiDocumentsPortal.reducerPath]: apiDocumentsPortal.reducer,
    [apiOrchestratorPricingCommittee.reducerPath]:
      apiOrchestratorPricingCommittee.reducer,
    [apiPortalPricing.reducerPath]: apiPortalPricing.reducer,
    userStore: userReducer,
    zoneStore: zoneReducer,
    alertStore: alertReducer,
    cardsStore: approverCardsSlice,
    searchStore: searchReducer,
    revenueStore: revenueReducer,
    domainApproverTask: domainApproverTaskSlice.reducer,
    automationStore: automationReducer,
  });

  const persistedReducer = persistReducer<Temp2>(
    {
      key: 'zones',
      storage,
      blacklist: ['automationStore'],
      transforms: [
        expireReducer('userStore', { expireSeconds: 3600 * 4 }),
        expireReducer('zoneStore', { expireSeconds: 3600 * 4 }),
      ],
      stateReconciler: autoMergeLevel2<Temp2>,
    },
    reducer
  );

  const store = configureStore({
    reducer: persistedReducer,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(
        apiApproverPortal.middleware,
        apiDocumentsPortal.middleware,
        apiOrchestratorPricingCommittee.middleware,
        apiPortalPricing.middleware
      ),
  });

  const persistor = persistStore(store);

  if (process.env.REACT_ENV !== 'production' && module?.hot) {
    module.hot.accept(
      ['./reducers/userReducer', './reducers/zoneReducer'],
      () => store.replaceReducer(persistedReducer)
    );
  }

  return { store, persistor };
}

export default configureAppStore;
